import { useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "tailwind-variants";

import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { Button } from "@design-lab/shared/ui";
import { Tooltip, TooltipTrigger } from "@design-lab/shared/ui/design-system/Tooltip";

export type PrintPerfectExplanatoryTooltipProps = {
  setWidth?: string;
  label?: string;
  centeredMobile?: boolean;
  buttonClassName?: string;
  children: ReactNode;
};

const DEFAULT_TOOLTIP_WIDTH = "580px";
const TOOLTIP_DELAY = 300;

export function PrintPerfectExplanatoryTooltip({
  setWidth,
  label = "Learn More",
  centeredMobile = false,
  buttonClassName,
  children
}: PrintPerfectExplanatoryTooltipProps) {
  const { isDesktop } = useResponsive();
  const [isHintOpen, setIsHintOpen] = useState(false);

  const handleClose = () => setIsHintOpen(false);
  const handleOpen = () => setIsHintOpen(true);

  // Desktop: Use regular tooltip
  if (isDesktop) {
    return (
      <TooltipTrigger isOpen={isHintOpen} delay={TOOLTIP_DELAY} onOpenChange={setIsHintOpen}>
        <Button
          data-testid="expert-edit-tooltip-button"
          variant="link"
          className={
            cn(
              "pr-1 text-blue-700 underline hover:bg-transparent hover:text-blue-700",
              buttonClassName
            ) as string
          }
          aria-label="Expert Edit information"
          onPress={handleOpen}
        >
          {label}
        </Button>
        <Tooltip
          className="text-primary bg-background justify-center rounded-lg border border-gray-200 p-4 text-sm shadow-lg"
          style={{ width: setWidth || DEFAULT_TOOLTIP_WIDTH }}
          showArrow={false}
          placement="right"
          crossOffset={-10}
        >
          {children}
        </Tooltip>
      </TooltipTrigger>
    );
  }

  // Mobile: Use modal-like bottom overlay
  return (
    <>
      <Button
        data-testid="expert-edit-tooltip-button"
        variant="link"
        className={
          cn(
            "pr-1 text-xs text-blue-700 underline hover:bg-transparent hover:text-blue-700",
            buttonClassName
          ) as string
        }
        aria-label="Expert Edit information"
        onPress={handleOpen}
      >
        {label}
      </Button>

      {isHintOpen && (
        <div
          data-testid="tooltip-overlay"
          className={cn(
            "fixed inset-0 z-[31] flex",
            centeredMobile ? "items-center justify-center px-2" : "items-end pb-5"
          )}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- presentational dismiss overlay; the dialog has a real Close button that handles keyboard dismissal */}
          <div
            className="bg-foreground/60 absolute inset-0"
            data-testid="tooltip-backdrop"
            onClick={handleClose}
          />
          <div
            className={cn(
              "bg-background relative w-full rounded-lg px-2.5 py-4",
              "animate-in slide-in-from-bottom-4 duration-300",
              "max-h-[95dvh] overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]"
            )}
          >
            <Button
              variant="icon"
              onPress={handleClose}
              aria-label="Close"
              className="absolute right-3 top-3 p-0"
              data-testid="expert-edit-tooltip-close-button"
            >
              <X className="scale-140" />
            </Button>

            <div className="text-primary text-sm">{children}</div>
          </div>
        </div>
      )}
    </>
  );
}
